import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { RotateCcw } from 'lucide-react'
import { getSaleDetail } from '../../api/sales'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { BackLink, DocHeader, StatusBadge } from '../../components/PageParts'
import { fmtDateTime } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money, num, saleNo, statusOf } from './salesShared'
import { useSession } from '../../session'

const STATUS_LABEL = { posted: 'Completed', refunded: 'Refund', draft: 'Unfinished', voided: 'Voided', void: 'Voided' }

export default function SaleDetail() {
  const { has } = useSession()
  const books = has('accounting') // the plan has Accounting: show accounts and journal entries
  const { saleId } = useParams()
  const [header, setHeader] = useState(null)
  const [lines, setLines] = useState([])
  const [addedLines, setAddedLines] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getSaleDetail(saleId)
      .then((data) => {
        if (cancelled) return
        setHeader(data?.header || null)
        setLines(data?.lines || [])
        setAddedLines(data?.added_lines || [])
        setError('')
      })
      .catch((e) => { if (!cancelled) setError(e.message || 'Could not load this sale.') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [saleId])

  const status = statusOf(header)
  const isRefund = status === 'refunded' || Boolean(header?.original_sale_id)
  const sold = (l) => num(l.sold_qty ?? l.sold)
  const refunded = (l) => num(l.refunded_qty ?? l.refunded)
  const canRefund = status === 'posted' && lines.some((l) => num(l.remaining_qty ?? l.remaining ?? (sold(l) - refunded(l))) > 0)
  const anyRefunded = lines.some((l) => refunded(l) > 0)
  const meta = header && [
    fmtDateTime(header.sale_datetime),
    header.location_name,
    header.payment_method && `Paid by ${header.payment_method}`,
  ].filter(Boolean).join(' · ')

  return (
    <div className="ci-form-page">
      <DocHeader
        back={<BackLink to="/sales/list" label="Sales" />}
        title={saleNo(header) || (isRefund ? 'Refund' : 'Sale')}
        badge={header && <StatusBadge status={status} label={STATUS_LABEL[status] || header.status} />}
        meta={meta && (
          <>
            {meta}
            {header.original_sale_id && <> · for <Link to={`/sales/detail/${header.original_sale_id}`}>{docNo('sale', header.original_sale_no)}</Link></>}
            {books && header.je_id && <> · <Link to={`/accounting/journal/${header.je_id}`}>Journal entry {docNo('je', header.je_no)}</Link></>}
          </>
        )}
        actions={canRefund && (
          <Link className="ci-btn ci-btn-primary" to={`/sales/refund/${saleId}`}><RotateCcw size={14} />Refund items</Link>
        )}
      />

      {error && <ErrorBanner message={error} />}

      {loading ? <Spinner label="Loading sale…" /> : header && (
        <>
          {status === 'draft' && (
            <p className="doc-note">This sale was never completed, so it isn't in your takings or your books yet.</p>
          )}

          <section className="doc-card">
            <div className="doc-card-head">
              <h2>Items</h2>
              <span>{lines.length} {lines.length === 1 ? 'item' : 'items'}</span>
            </div>
            <div className="doc-table-scroll">
              <table className="ci-line-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="num">Quantity</th>
                    {anyRefunded && <th className="num">Refunded</th>}
                    <th className="num">Price</th>
                    <th className="num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={`${l.product_id}-${i}`}>
                      <td>{l.name}<span className="cell-sub">{l.sku}</span></td>
                      <td className="num">{Math.abs(sold(l)).toLocaleString()}</td>
                      {anyRefunded && <td className="num">{refunded(l) ? refunded(l).toLocaleString() : '–'}</td>}
                      <td className="num">{l.unit_price != null ? money(l.unit_price) : '–'}</td>
                      <td className="num ci-total">{l.unit_price != null ? money(Math.abs(sold(l)) * num(l.unit_price)) : '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="ci-detail-total">
              <span>Total</span>
              <strong>{isRefund ? '−' : ''}{money(Math.abs(num(header.total_amount)))}</strong>
            </div>
          </section>

          {addedLines.length > 0 && (
            <section className="doc-card">
              <div className="doc-card-head">
                <h2>Stock added during this sale</h2>
                <span>Stock showed too few of these when it was sold, so stock was added to cover the sale.</span>
              </div>
              <div className="doc-table-scroll">
                <table className="ci-line-table">
                  <thead>
                    <tr><th>Item</th><th className="num">Added</th><th /></tr>
                  </thead>
                  <tbody>
                    {addedLines.map((l, i) => (
                      <tr key={`${l.product_id}-${i}`}>
                        <td>{l.name}<span className="cell-sub">{l.sku}</span></td>
                        <td className="num">{num(l.added_qty).toLocaleString()}</td>
                        <td className="num">{books && l.je_id ? <Link className="ci-je-link" to={`/accounting/journal/${l.je_id}`}>Journal entry {docNo('je', l.je_no)}</Link> : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  )
}
